import { useCallback, useEffect } from 'react';
import {
    KameleoonError,
    KameleoonException,
    useFeatureFlag,
    useInitialize,
    useVisitorCode,
} from '@kameleoon/react-sdk';
import { captureError } from '@lingoda/monitor';
import { parseFeaturesFromKameleoon, shouldSetTrueKameleoonConsent } from './utils';
import type { KameleoonFlagKey, KameleoonState } from './types';
import type { KameleoonVariationType } from '@kameleoon/react-sdk';

interface InitializeOptions {
    setKameleoonState: (state: KameleoonState) => void;
}
export const useKameleoonInitialize = ({ setKameleoonState }: InitializeOptions) => {
    const { initialize } = useInitialize();
    const { getVisitorCode, setLegalConsent } = useVisitorCode();
    const { getActiveFeatureFlags } = useFeatureFlag();

    const init = useCallback(async (): Promise<void> => {
        try {
            await initialize();
            const code = getVisitorCode();

            const activeFlags = getActiveFeatureFlags(code) as Map<
                KameleoonFlagKey,
                KameleoonVariationType
            >;

            const newFeatures = parseFeaturesFromKameleoon(activeFlags);

            setKameleoonState({
                status: 'initialized',
                features: newFeatures,
                visitorCode: code,
            });
        } catch (error) {
            setKameleoonState({ status: 'failed' });
            if (
                !(error instanceof KameleoonError) ||
                error.type !== KameleoonException.ClientConfiguration
            ) {
                throw error;
            }
        }
    }, [getActiveFeatureFlags, getVisitorCode, initialize, setKameleoonState]);

    useEffect(() => {
        const initialize = async () => {
            try {
                await init();
                const code = getVisitorCode();
                if (shouldSetTrueKameleoonConsent()) {
                    setLegalConsent(code, true);
                }
            } catch (error) {
                captureError(error);
            }
        };

        void initialize();
    }, [getVisitorCode, init, setLegalConsent]);
};
